import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {Store} from "@ngrx/store";
import {
    selectDiscountMultipleValuation,
    selectDmvAlgorithmConfidenceZone,
    selectNotAssignedCapex,
    selectRequiredFieldsForAlgorithmLookup,
    selectSelectedValuationError,
    selectActiveAssetValuation
} from "../../../store/asset-valuation/asset-valuation.selectors";
import {AssetValuationDetailActions} from "../../../store/asset.actions";
import {filter, map, Observable, Subscription, switchMap} from "rxjs";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {AssetValuationScenario} from "../../../models/asset-valuation/asset-valuation-scenario";
import {AssetValuation, EMPTY_ASSET_VALUATION} from "../../../models/asset-valuation/asset-valuation";
import {AssetCashflow} from "../../../models/asset-valuation/asset-cashflow";
import {DecimalFormatPipe} from "../../../../shared/pipes/decimal-format/decimal-format.pipe";
import {DateUtil} from "../../../../shared/utils/date-util";
import {selectGeneralPartnerNav, selectIsAnyAssetFormInEditMode, selectRelatedFundCurrencyIsoCode, selectSelectedAssetId} from "../../../store/asset/asset.selectors";
import {ConfidenceZone, isValidValuationInput, RequiredDMVAlgoFields, ValuationInput} from "../../../models/asset-valuation/discount-multiple-valuation";
import {MatDialog} from "@angular/material/dialog";
import {AssetCashflowNewDialogComponent} from "../asset-cashflow-new-dialog/asset-cashflow-new-dialog.component";
import {CashflowTypeEnum} from "../../../models/asset-valuation/cashflow-type.enum";
import {AssetValuationTypeEnum} from "../../../models/asset-valuation/asset-valuation-type.enum";
import {CodeTableEnum} from "../../../../shared/model/code";
import {selectCodeTranslation} from "../../../../shared/store/shared.selectors";
import {take} from "rxjs/operators";
import {ConfirmationComponent} from "../../../../shared/components/confirmation/confirmation.component";

@Component({
    selector: "valumize-asset-discount-multiple-valuation",
    templateUrl: "./asset-discount-multiple-valuation.component.html",
    styleUrls: ["./asset-discount-multiple-valuation.component.scss"],
    standalone: false
})
export class AssetDiscountMultipleValuationComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);

    subscriptions: Subscription[] = [];

    selectedAssetValuation$ = this.store.select(selectActiveAssetValuation);
    discountMultipleValuation$ = this.store.select(selectDiscountMultipleValuation);
    gpNav$ = this.store.select(selectGeneralPartnerNav);
    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    requiredFieldsForAlgorithmLookup$ = this.store.select(selectRequiredFieldsForAlgorithmLookup);
    notAssignedCapex$: Observable<{ LOW: number; BASE: number; HIGH: number }> = this.store.select(selectNotAssignedCapex);
    dmvError$ = this.store.select(selectSelectedValuationError);
    confidenceZone$ = this.store.select(selectDmvAlgorithmConfidenceZone);

    persistedScenarios: AssetValuationScenario[] = [];
    persistedAssetValuation: AssetValuation = EMPTY_ASSET_VALUATION;
    calculatedScenarios: AssetValuationScenario[] = [];
    calculatedAssetValuation: AssetValuation = EMPTY_ASSET_VALUATION;
    codeTableCashflowType = CodeTableEnum.SHARED_CASHFLOWTYPE;
    selectedValuationInput: ValuationInput = "MULTIPLE";
    fundCurrency: string = "";
    isEditable = false;
    private wasEditable = false;
    rationaleIsEditable = false;
    isDisabledscenarioInput = false;
    pendingSave = false;
    nextFocus: string | undefined;
    private static readonly DYNAMIC_CONTROL_SUFFIX_PATTERN = /^(?:lowCalc|baseCalc|highCalc)-(.+)$/;
    private static readonly STATIC_FOCUS_CONTROLS = ["calculatedRequiredGrossIRR", "valuationInputToggle", "mostRecentCashflowDate"];
    controlNamesForFocus = [...AssetDiscountMultipleValuationComponent.STATIC_FOCUS_CONTROLS];

    discountMultipleValuationForm:
        FormGroup<{ mostRecentCashflowDate: FormControl<Date | null>; calculatedRequiredGrossIRR: FormControl<number | null> }
            & { [key: string]: FormControl<any> }> =

        this.formBuilder.group({
            mostRecentCashflowDate: this.formBuilder.control<Date | null>(null, {nonNullable: true, validators: [Validators.required]}),
            calculatedRequiredGrossIRR: this.formBuilder.control<number | null>(null, {nonNullable: true, validators: [Validators.required, Validators.min(0), Validators.max(99)]})
        });

    ngOnInit() {
        this.subscriptions.push(this.store.select(selectRelatedFundCurrencyIsoCode).pipe(map((currency) => {
            this.fundCurrency = currency ?? "";
        })).subscribe());

        this.subscriptions.push(this.discountMultipleValuation$.pipe(map((discountMultipleValuation) => {
            this.isEditable = discountMultipleValuation.discountMultipleValuationEditable;
            this.rationaleIsEditable = discountMultipleValuation.rationaleIsEditable;

            this.persistedAssetValuation = discountMultipleValuation.persistedAssetValuation;
            this.persistedScenarios = discountMultipleValuation.persistedScenarios;

            // On ERROR the reducer wipes calc state. Skip the form re-patch so user input survives,
            // and keep the local calc caches at last-known-good so the next dispatch isn't empty
            // (an empty scenarios payload would re-trigger requireAllScenarios in a loop).
            if (discountMultipleValuation.calcStatus !== "ERROR") {
                this.calculatedAssetValuation = discountMultipleValuation.calcAssetValuation;
                this.calculatedScenarios = discountMultipleValuation.calcScenarios;

                const rowValue = discountMultipleValuation.tableDataSource.filter(row => row.definition === "cashflows");

                // Drop orphan dynamic controls so allScenariosFilled() doesn't keep Save disabled.
                // On edit-mode entry, drop ALL dynamic controls too — the reducer resets
                // calcScenarios to scenarios on edit, and addControl is a no-op for existing names,
                // so a clear-then-cancel would otherwise leave the cell stale/red.
                const enteredEditMode = !this.wasEditable && this.isEditable;
                const liveSuffixes = new Set(rowValue.map(row => row.name));
                // Cast: the typed FormGroup disallows removeControl on declared keys.
                const formGroup = this.discountMultipleValuationForm as unknown as FormGroup;
                Object.keys(formGroup.controls).forEach(name => {
                    const match = AssetDiscountMultipleValuationComponent.DYNAMIC_CONTROL_SUFFIX_PATTERN.exec(name);
                    if (match && (enteredEditMode || !liveSuffixes.has(match[1]))) {
                        formGroup.removeControl(name);
                    }
                });
                // Reset before the push-only loop below so the list doesn't grow across edits.
                this.controlNamesForFocus = [...AssetDiscountMultipleValuationComponent.STATIC_FOCUS_CONTROLS];

                if (rowValue) {
                    rowValue.forEach(row => {
                        const identifier = row.name;
                        const patchData = {
                            [`lowCalc-${identifier}`]: row.lowCalc as AssetCashflow,
                            [`baseCalc-${identifier}`]: row.baseCalc as AssetCashflow,
                            [`highCalc-${identifier}`]: row.highCalc as AssetCashflow
                        };
                        Object.entries(patchData).forEach(([controlName, value]) => {
                            // Convert calc actual-currency to millions-display so user-typed and
                            // calc-refreshed cells share the same unit; otherwise updateScenario
                            // would double-transform the latter on the next dispatch.
                            const formValue = this.toFormValue(value);
                            const existing = this.discountMultipleValuationForm.get(controlName);
                            if (existing) {
                                if (existing.pristine) {
                                    existing.setValue(formValue ?? null, {emitEvent: false});
                                } else if (formValue) {
                                    // Dirty: keep the user-typed active field, refresh the inactive
                                    // one from the calc so a mode-switch doesn't render the cell
                                    // empty/red for a value the calc supplied.
                                    const current = existing.value || {};
                                    const merged = this.isCashflowValuation()
                                        ? {...formValue, amount: current.amount ?? formValue.amount}
                                        : {...formValue, navMultiple: current.navMultiple ?? formValue.navMultiple};
                                    existing.setValue(merged, {emitEvent: false});
                                }
                            } else {
                                this.discountMultipleValuationForm.addControl(
                                    controlName,
                                    this.formBuilder.control<AssetCashflow | null>(formValue ?? null, {nonNullable: true, validators: [Validators.required]})
                                );
                            }
                        });
                        this.controlNamesForFocus.push(`lowCalc-${identifier}`, `baseCalc-${identifier}`, `highCalc-${identifier}`);
                    });
                }

                this.discountMultipleValuationForm.patchValue({
                    // No hardcoded fallback: the required gross IRR default comes from the
                    // asset.requiredGrossIRR.default setting (stamped at DMV creation). When neither the
                    // setting nor a prior value supplies one, the field stays empty and required so the
                    // user enters it explicitly, rather than silently assuming a rate.
                    calculatedRequiredGrossIRR: DecimalFormatPipe.transformFractionToPercent(discountMultipleValuation.calcAssetValuation.requiredGrossIRR.fraction),
                    mostRecentCashflowDate: discountMultipleValuation.mostRecentCalcCashflowDate
                });

                // Disable cashflow input fields, when no date exists
                this.isDisabledscenarioInput = !discountMultipleValuation.mostRecentCalcCashflowDate;
            }

            if (this.isEditable) {
                // Use a setTimeout to refocus on the next Javascript event loop tick,
                // giving Angular a chance to update the DOM first
                setTimeout(() => {
                    if (this.nextFocus) {
                        this.pendingSave = false;
                        const element = document.getElementById(this.nextFocus) as HTMLInputElement;
                        if (element) {
                            element.focus();
                            element.select();
                        } else {
                            console.warn(`Element with id "${this.nextFocus}" not found`);
                        }
                    }
                }, 0);
                this.discountMultipleValuationForm.enable();
            } else {
                this.discountMultipleValuationForm.disable();
            }
            this.wasEditable = this.isEditable;
        })).subscribe());
    }

    onValuationInputChange = (valuationInput: string) => {
        if (isValidValuationInput(valuationInput)) {
            this.selectedValuationInput = valuationInput;
            this.ngOnInit();
        }
    };

    isCashflowValuation = () => this.selectedValuationInput === "CF";

    navExists = (nav?: number | null) => nav !== undefined && nav !== null;

    // The algorithm calc dispatches even when the form is invalid (calcAndSave withAlgorithm),
    // and the backend requireNonNulls the required gross IRR fraction — so the empty field must
    // gate the button here, mirroring canSave() for the Save button.
    private readonly hasRequiredGrossIRR = (): boolean =>
        this.discountMultipleValuationForm.controls.calculatedRequiredGrossIRR.value != null;

    validateValuationLookup = (requiredFields: RequiredDMVAlgoFields, tableDataSource: any[]) => {
        const rowValue = tableDataSource.filter(row => row.definition === "cashflows");
        return rowValue.length === 1 && rowValue[0].name.includes("EXIT")
            && requiredFields.totalTVPI !== null && requiredFields.totalTVPI !== 0
            && requiredFields.investmentDate !== null && requiredFields.investmentDate.trim().length !== 0
            && requiredFields.generalPartnerQuality !== null && requiredFields.generalPartnerQuality.trim().length !== 0
            && this.hasRequiredGrossIRR();
    };

    noExitCashflow() {
        const cashflows = this.persistedScenarios.flatMap(scenario => scenario.cashflows);
        return cashflows.filter(cf => cf.cashflowType.code === "EXIT").length === 0
    }

    canSave(): boolean {
        if (this.discountMultipleValuationForm.invalid) {
            return false;
        }
        return this.allScenariosFilled();
    }

    private allScenariosFilled(): boolean {
        const formValues = this.discountMultipleValuationForm.getRawValue();
        const cashflowSuffixes = Object.keys(formValues)
            .filter(name => name.startsWith("lowCalc-"))
            .map(name => name.substring("lowCalc-".length));

        if (cashflowSuffixes.length === 0) {
            return false;
        }
        if (!cashflowSuffixes.some(suffix => suffix.startsWith("EXIT"))) {
            return false;
        }

        return cashflowSuffixes.every(suffix =>
            this.isAssetCashflowFilled(formValues[`lowCalc-${suffix}`])
            && this.isAssetCashflowFilled(formValues[`baseCalc-${suffix}`])
            && this.isAssetCashflowFilled(formValues[`highCalc-${suffix}`])
        );
    }

    private isAssetCashflowFilled(cf: AssetCashflow | null | undefined): boolean {
        if (!cf) {
            return false;
        }
        // Check only the active field for the current mode; a stale value in the inactive
        // field from a prior calc must not mask a cleared cell.
        return this.isCashflowValuation()
            ? cf.amount?.amount !== null && cf.amount?.amount !== undefined
            : cf.navMultiple?.factor !== null && cf.navMultiple?.factor !== undefined;
    }

    isCellEmpty(focus: string, element: any, type: "currency" | "multiple"): boolean {
        const controlName = this.getFormControl(focus, element);
        const control = this.discountMultipleValuationForm.get(controlName);
        // Don't flag pristine cells: the red cue is for cells the user touched and left empty,
        // not for the initial empty state of a fresh DMV.
        if (!control || control.pristine) {
            return false;
        }
        const value = control.value;
        if (!value) {
            return true;
        }
        return type === "currency"
            ? value.amount?.amount === null || value.amount?.amount === undefined
            : value.navMultiple?.factor === null || value.navMultiple?.factor === undefined;
    }

    confidenceZoneLabel = (zone: ConfidenceZone): string =>
        zone === "OUTSIDE_RANGE" ? "Outside calibrated range" : "Near calibrated range";

    confidenceZoneTooltip = (zone: ConfidenceZone): string =>
        zone === "OUTSIDE_RANGE"
            ? "TVPI and/or holding period lie outside the algorithm's calibrated range; the suggested values were clamped at the calibration edge — review manually."
            : "TVPI and/or holding period lie just beyond the algorithm's calibrated range; the suggested values were extrapolated from the calibration edge.";

    tootTipMessage = (requiredFields: RequiredDMVAlgoFields, tableDataSource: any[]): string => {
        const rowValue = tableDataSource.filter(row => row.definition === "cashflows");
        if (!(rowValue.length === 1 && rowValue[0].name.includes("EXIT"))) {
            return "Algorithm Lookup is only supported if a single exit cashflow is present. Does not work with other cashflow types.";
        }

        const missingFields: string[] = [];
        if (requiredFields.totalTVPI === null || requiredFields.totalTVPI === 0) {
            missingFields.push("Total TVPI");
        }
        if (requiredFields.investmentDate === null || requiredFields.investmentDate.trim().length === 0) {
            missingFields.push("Investment Date");
        }
        if (requiredFields.generalPartnerQuality === null || requiredFields.generalPartnerQuality.trim().length === 0) {
            missingFields.push("General Partner Quality");
        }
        if (!this.hasRequiredGrossIRR()) {
            missingFields.push("Required Gross IRR");
        }
        return missingFields.length === 0 ? "" : `Missing fields: ${missingFields.join(", ")}`;
    };

    getFormControl = (prefix: string, element: any): string => `${prefix}-${element.name}`;

    // (input): syncs the form per keystroke so canSave() re-evaluates without waiting for blur.
    // Empty input writes null so allScenariosFilled() recognises the cell as missing.
    onFormValueInput(event: Event, element: any, type: "currency" | "multiple", focus: string): void {
        const controlName = this.getFormControl(focus, element);
        const rawValue = (event.target as HTMLInputElement).value;
        const parsedValue = rawValue === "" ? null : parseFloat(rawValue);
        if (parsedValue !== null && isNaN(parsedValue)) {
            return;
        }
        const control = this.discountMultipleValuationForm.get(controlName);
        if (!control) {
            return;
        }
        const currentValue = control.value || {};
        if (type === "currency") {
            control.setValue({...currentValue, amount: {...currentValue.amount, amount: parsedValue}});
        } else {
            control.setValue({...currentValue, navMultiple: {...currentValue.navMultiple, factor: parsedValue}});
        }
        // Mark dirty so the next emission's pristine-only refresh won't clobber user input
        // (including a deliberate null clear).
        control.markAsDirty();
    }

    // (change): fires on commit (Enter / blur). Gated to commit so a fast typist doesn't fire
    // one calcDMV per keystroke (exhaustMap would drop the later ones, leaving partial state).
    onFormValueChange(event: Event, element: any, type: "currency" | "multiple", focus: string): void {
        const controlName = this.getFormControl(focus, element);
        if (focus.length > 0) {
            const currentIndex = this.controlNamesForFocus.findIndex(control => control === controlName);
            this.nextFocus = this.controlNamesForFocus[currentIndex + 1] || this.controlNamesForFocus[0];
        }

        // Re-apply via (input) in case the user committed without intermediate (input) events
        // (e.g. paste-and-Enter).
        this.onFormValueInput(event, element, type, focus);

        if (isNaN(parseFloat((event.target as HTMLInputElement).value))) {
            return;
        }

        this.pendingSave = true;
        // Hold the dispatch until every scenario is fillable; otherwise the backend rejects
        // and the ERROR reducer wipes calc state, dropping the value the user just typed.
        if (this.allScenariosFilled()) {
            this.calcAndSave(controlName);
        } else {
            this.focusNextInput();
        }
    }

    private toFormValue(value: AssetCashflow | null | undefined): AssetCashflow | null {
        if (!value) {
            return null;
        }
        return {
            ...value,
            amount: {
                ...value.amount,
                amount: DecimalFormatPipe.transformFromMillionsNum(value.amount?.amount)
            }
        };
    }

    private markDynamicControlsPristine(): void {
        const formGroup = this.discountMultipleValuationForm as unknown as FormGroup;
        Object.keys(formGroup.controls).forEach(name => {
            if (AssetDiscountMultipleValuationComponent.DYNAMIC_CONTROL_SUFFIX_PATTERN.exec(name)) {
                formGroup.controls[name].markAsPristine();
            }
        });
    }

    private focusNextInput(): void {
        if (!this.nextFocus) {
            return;
        }
        const next = this.nextFocus;
        setTimeout(() => {
            const element = document.getElementById(next) as HTMLInputElement | null;
            element?.focus();
            element?.select();
        }, 0);
    }

    calcAndSave(controlName: string, save = false, withAlgorithm = false) {
        // Disable cashflow input fields, when no date exists
        this.isDisabledscenarioInput = !this.discountMultipleValuationForm.controls.mostRecentCashflowDate.value;

        const updatedAssetValuation = this.assetValuationForCalc();
        let updatedScenarios: AssetValuationScenario[] = [];

        // darf mit Datum nur ausgeführt werden wenn das Datum auch gesetzt ist und die Cashflows nicht leer sind
        if (
            this.persistedScenarios[0].cashflows.length === 0
            && (controlName === "mostRecentCashflowDate")
            && (
                !this.calculatedScenarios.find(s => s.scenario.code === "LOW")?.cashflows.length
                && !this.calculatedScenarios.find(s => s.scenario.code === "BASE")?.cashflows.length
                && !this.calculatedScenarios.find(s => s.scenario.code === "HIGH")?.cashflows.length
            )
        ) {
            return;
        } else {
            updatedScenarios = (controlName !== "mostRecentCashflowDate")
                ? this.scenarioForCalc(this.calculatedScenarios, this.discountMultipleValuationForm, controlName)
                : this.calculatedScenarios;
        }

        if (withAlgorithm || (!withAlgorithm && this.discountMultipleValuationForm.valid)) {
            if (withAlgorithm) {
                // Algorithm overwrites every scenario's EXIT navMultiple — the user invoked it
                // to "fill everything", so let the next emission's pristine-only refresh apply.
                this.markDynamicControlsPristine();
            }
            this.store.dispatch(
                AssetValuationDetailActions.calcDMV({
                    assetValuation: updatedAssetValuation,
                    scenarios: updatedScenarios,
                    save,
                    valuationInput: this.selectedValuationInput,
                    withAlgorithm
                })
            );
        }

        if (save && this.pendingSave) {
            setTimeout(() => {
                this.calcAndSave(controlName, true);
            }, 0);
        }
    }

    assetValuationForCalc(): AssetValuation {
        const mostRecentCashflowDate =
            DateUtil.toIsoDate(this.discountMultipleValuationForm.controls.mostRecentCashflowDate.value)
            ?? this.persistedAssetValuation.exitDateAssumption.date;

        const requiredGrossIRR =
            DecimalFormatPipe.transformPercentToFraction(this.discountMultipleValuationForm.controls.calculatedRequiredGrossIRR.value)
            ?? this.persistedAssetValuation.requiredGrossIRR.fraction;

        return {
            ...this.calculatedAssetValuation,
            requiredGrossIRR: {
                ...this.calculatedAssetValuation.requiredGrossIRR,
                fraction: requiredGrossIRR
            },
            exitDateAssumption: {
                ...this.calculatedAssetValuation.exitDateAssumption,
                date: mostRecentCashflowDate
            }
        };
    }

    scenarioForCalc(calcScenarios: AssetValuationScenario[], form: any, focus: string): AssetValuationScenario[] {
        const scenarioCodes = ["LOW", "BASE", "HIGH"];
        const updatedFormValues = form.getRawValue();
        const updatedScenarios: AssetValuationScenario[] = [];

        // Only apply form values for cells the user has typed in (dirty). Pristine cells were
        // last refreshed by a calc with values in actual currency; routing them through
        // updateScenario would re-apply transformToMillionsNum (×1M) and corrupt the dispatch.
        // Use the existing scenario.cashflows for pristine cells instead.
        const suffixMatch = AssetDiscountMultipleValuationComponent.DYNAMIC_CONTROL_SUFFIX_PATTERN.exec(focus);
        const focusedSuffix = suffixMatch ? suffixMatch[1] : null;

        scenarioCodes.forEach(code => {
            const scenario = calcScenarios.find(s => s.scenario.code === code);
            if (scenario) {
                const formKey = focusedSuffix ? `${code.toLowerCase()}Calc-${focusedSuffix}` : null;
                const formControl = formKey ? form.get(formKey) : null;
                const cashflowValue = formKey && formControl?.dirty ? updatedFormValues[formKey] : undefined;
                const updatedScenario = cashflowValue
                    ? this.updateScenario(scenario, cashflowValue)
                    : scenario;

                updatedScenarios.push(updatedScenario);
            }
        });
        return updatedScenarios;
    }

    deleteCashflows(element: any) {
        const updatedScenarios = this.persistedScenarios.map(scenario => {
            const scenarioCode = scenario.scenario.code;
            if (scenarioCode) {
                return {
                    ...scenario,
                    cashflows: scenario.cashflows.filter(cashflow => cashflow.id !== element[scenarioCode.toLowerCase()].id)
                };
            }
            return scenario;
        });

        this.store.dispatch(AssetValuationDetailActions.calcDMV({
            assetValuation: this.persistedAssetValuation,
            scenarios: updatedScenarios,
            save: true,
            valuationInput: this.selectedValuationInput,
            withAlgorithm: false
        }));
    }

    onResetValuation() {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = "Reset Valuation?";
        dialogRef.componentInstance.htmlContent = "This will clear all calculated values and scenarios. User-entered parameters will be preserved.";

        this.subscriptions.push(dialogRef.afterClosed().pipe(
            take(1),
            filter(result => !!result),
            switchMap(() => this.store.select(selectSelectedAssetId).pipe(take(1)))
        ).subscribe(assetId => {
            if (assetId && this.persistedAssetValuation.id) {
                this.store.dispatch(AssetValuationDetailActions.resetValuation({
                    assetId,
                    assetValuationId: this.persistedAssetValuation.id
                }));
            }
        }));
    }

    editMode = () => this.store.dispatch(AssetValuationDetailActions.edit());

    cancel = () => this.store.dispatch(AssetValuationDetailActions.cancel());

    openNewCashflowDialog() {
        const dialogRef = this.dialog.open(AssetCashflowNewDialogComponent);
        dialogRef.componentInstance.assetValuation = this.persistedAssetValuation;
        dialogRef.componentInstance.scenarios = this.persistedScenarios;
        dialogRef.componentInstance.valuationType = AssetValuationTypeEnum.DiscountMultipleValuation;
    }

    formControlIsEditable(element: any): boolean {
        const editableRows = ["cashflows"];
        return editableRows.includes(element.definition) && this.isEditable;
    }

    deletableRow(element: any): boolean {
        const cashflowRows = ["cashflows"];
        return cashflowRows.includes(element.definition) && Object.values(CashflowTypeEnum).some(enumValue => element.name.includes(enumValue));
    }

    translateCashflowType(cashflowNameString: string): Observable<string> {
        const [cashflowTypeCode, date] = cashflowNameString.trim().split(/\s+/);

        return this.store.select(selectCodeTranslation(cashflowTypeCode, this.codeTableCashflowType)).pipe(
            // immediate fallback to the code if no translation yet
            map((t) => t || cashflowTypeCode),
            take(1),
            map((translated) => {
                const datePart = DateUtil.formatIsoDate?.(date) ?? "";
                return datePart ? `${datePart} - ${translated}` : translated;
            })
        );
    }

    styleRowImportant(row: any): string {
        const importantRowTypes = ["totalCashflow", "navMultiple"];
        return (importantRowTypes.includes(row.definition)) ? "row-important" : "";
    }

    styleRowBorderTop(row: any): string {
        const borderTopRowTypes = ["totalCashflow"];
        return (borderTopRowTypes.includes(row.definition)) ? "row-top-border" : "";
    }

    styleRowBorderBottom(row: any): string {
        const borderTopRowTypes = ["probability"];
        return (borderTopRowTypes.includes(row.definition)) ? "row-bottom-border" : "";
    }

    onEditRationale = () => this.store.dispatch(AssetValuationDetailActions.editRationale());

    onCancelRationale = () => this.store.dispatch(AssetValuationDetailActions.cancelRationale());

    onSaveRationale = (assetValuation: AssetValuation, rationale: string) => {
        if (assetValuation.id && assetValuation.valuationType.code) {
            this.store.dispatch(AssetValuationDetailActions.saveRationale({
                assetValuationId: assetValuation.id,
                rationale,
                valuationType: assetValuation.valuationType.code
            }));
        }
    }

    ngOnDestroy = (): void => this.subscriptions.forEach(subscription => subscription.unsubscribe());

    private updateScenario(scenario: AssetValuationScenario, assetCashflow?: AssetCashflow): AssetValuationScenario {
        if (assetCashflow && assetCashflow.id === undefined) {
            const exitCashflow = this.isCashflowValuation()
                ? {
                    ...assetCashflow,
                    date: {...assetCashflow.date, date: DateUtil.toIsoDate(this.discountMultipleValuationForm.controls.mostRecentCashflowDate.value)},
                    amount: {
                        ...assetCashflow.amount,
                        currencyIso: this.fundCurrency,
                        amount: DecimalFormatPipe.transformToMillionsNum(assetCashflow.amount.amount)
                    }
                }
                : {
                    ...assetCashflow,
                    date: {...assetCashflow.date, date: DateUtil.toIsoDate(this.discountMultipleValuationForm.controls.mostRecentCashflowDate.value)},
                    navMultiple: {...assetCashflow.navMultiple, factor: assetCashflow.navMultiple.factor}
                };
            return {
                ...scenario,
                cashflows: [exitCashflow]
            };
        }

        return {
            ...scenario,
            cashflows: scenario.cashflows.map(cashflow => {
                if (assetCashflow && assetCashflow.id === cashflow.id) {
                    return this.isCashflowValuation()
                        ? {
                            ...cashflow,
                            amount: {
                                ...cashflow.amount,
                                currencyIso: this.fundCurrency,
                                amount: DecimalFormatPipe.transformToMillionsNum(assetCashflow.amount.amount)
                            }
                        }
                        : {
                            ...cashflow,
                            navMultiple: {
                                ...cashflow.navMultiple,
                                factor: assetCashflow.navMultiple.factor
                            }
                        };
                }
                return cashflow;
            })
        };
    }
}
